import {
  AtsCandidate,
  FilterState,
  HydratedRow,
  MatchBand,
  PoolInfo,
  RecsysFilters,
  RecsysHttpError,
  RecsysPoolSummary,
  SavedSearchSettingsV1,
  SavedSearchSettingsState,
  ScoreBand,
  SearchMode,
  SEARCH_SETTINGS_SCHEMA_VERSION,
} from './types';

// FilterState.radiusMi sentinel meaning "Any distance" (README: 10-250mi
// slider). Canonical definition — context.tsx re-exports it for the page.
export const MAX_RADIUS_MI = 250;

// Lower bound of the same slider.
export const MIN_RADIUS_MI = 10;

// Choices of the "Results (Top N)" select, shared by the Batch control card and
// the Search mode control (they drive the same topN state) and by the saved-
// settings sanitizer, which snaps a stored value onto this list so the select
// can never render an unlisted option.
export const TOP_N_OPTIONS = [10, 20, 50, 100];

// Canonical empty filter state — context.tsx re-exports it for the page (same
// pattern as MAX_RADIUS_MI). Lives here so the saved-settings sanitizer below
// can fall back to it without importing from context.tsx (a cycle).
export const DEFAULT_FILTERS: FilterState = {
  minYears: 0,
  skills: [],
  geoCenterLabel: '',
  geoCenterCustom: false,
  lat: null,
  lng: null,
  radiusMi: MAX_RADIUS_MI,
  radiusCustom: false,
};

/**
 * Buckets a raw recsys similarity score into a display band. Scores are
 * relative similarity, never a "% match" — see README §Business rules.
 * Strong >= 0.75, Moderate >= 0.55, else Weak. null (no score, e.g. boolean
 * search) maps to null so callers can render the boolean "matched" state.
 */
export const scoreBand = (score: number | null | undefined): ScoreBand | null => {
  if (score === null || score === undefined) return null;
  if (score >= 0.75) return 'strong';
  if (score >= 0.55) return 'moderate';
  return 'weak';
};

/**
 * Buckets an LLM judge score (Batch Matches, candidate.explain.llmScore) into
 * a match band. Strong >= 70, Possible 50-69, else Not a Match. null/undefined
 * (unscored or judged-but-failed — see RecsysExplain.status) maps to null so
 * callers can render the "unscored"/"not scored" state distinctly.
 */
export const matchBand = (llmScore: number | null | undefined): MatchBand | null => {
  if (llmScore == null) return null;
  if (llmScore >= 70) return 'strong';
  if (llmScore >= 50) return 'possible';
  return 'none';
};

/* ---- applicant pool (kan-119) ------------------------------------------- */

/**
 * The "N of M applicants searchable" numbers from a pool-restricted response.
 * null when the response carries no `pool` (an all-candidates call). M is the
 * pool size the ATS proxy reports; N is recsys's stats.poolInCorpus — absent on
 * the proxy's zero-applicant short-circuit (stats: {}), which reads as 0.
 */
export const poolInfoFromResponse = (response: {
  pool?: RecsysPoolSummary;
  stats?: { poolInCorpus?: number | null; [k: string]: unknown };
}): PoolInfo | null => {
  const pool = response.pool;
  if (!pool) return null;
  const inCorpus = response.stats?.poolInCorpus;
  return {
    applicants: pool.applicants,
    inCorpus: typeof inCorpus === 'number' && Number.isFinite(inCorpus) ? inCorpus : 0,
    truncated: pool.truncated === true,
  };
};

/**
 * JobAppStatus enum KEY -> display label for the applicant-mode row pill
 * ('CONTACT_LATER' -> 'Contact Later'). Display only; the key is what the
 * status mutations take.
 */
export const formatJobAppStatus = (status: string | null | undefined): string =>
  (status ?? '')
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/**
 * Applicant-mode CandidateWorkspacePanel props for the open row (kan-119).
 *
 * The candidate identity is ALWAYS passed, so the résumé/contacts render even
 * before jobApplicantsForCandidates resolves, after it fails, or for a row
 * with no application on this job. The applicant id/status/date are spread in
 * only once hydrated; until then `showDisabledDispositions` flips the panel
 * into its read-only mode, which hides Set Status + Decline — both handlers
 * dereference applicant.id, so they must never render against an id-less
 * applicant. The panel seeds its status badge from JobAppStatus only on open /
 * candidate change, so `key` changes when the applicant id lands to remount it
 * with the real status.
 */
export const applicantPanelProps = (
  item: Pick<HydratedRow, 'ats' | 'applicant'> | null | undefined
): {
  key: string;
  applicant: { id?: string; JobAppStatus?: string; createdAt?: string; candidate: AtsCandidate } | null;
  showDisabledDispositions: boolean;
} => {
  const app = item?.applicant;
  return {
    key: `applicant-panel:${app?.id ?? 'pending'}`,
    applicant: item?.ats
      ? {
          ...(app?.id ? { id: app.id, JobAppStatus: app.JobAppStatus, createdAt: app.createdAt } : {}),
          candidate: item.ats,
        }
      : null,
    showDisabledDispositions: !app?.id,
  };
};

/** Miles -> km, applied ONLY at the recsys API boundary (RecsysFilters.geo.radiusKm). */
export const milesToKm = (mi: number): number => mi * 1.60934;

/**
 * filters -> RecsysFilters at the API boundary. Converts the UI's miles
 * radius to km (STORY-2A-SPEC.md rule 4) and omits empty facets. "Any
 * distance" (radiusMi === MAX_RADIUS_MI) sends no geo filter at all.
 */
export const buildRecsysFilters = (filters: FilterState): RecsysFilters | undefined => {
  const out: RecsysFilters = {};
  if (filters.minYears > 0) out.minYearsExperience = filters.minYears;
  if (filters.skills.length) out.skills = filters.skills;
  if (filters.lat !== null && filters.lng !== null && filters.radiusMi < MAX_RADIUS_MI) {
    out.geo = { lat: filters.lat, lng: filters.lng, radiusKm: milesToKm(filters.radiusMi) };
  }
  return Object.keys(out).length ? out : undefined;
};

/**
 * Strips HTML tags out of a rich-text field (advertisedJobDescription is
 * authored in a rich-text editor) so it can be sent to recsys /match as plain
 * text. Mirrors packages/api/src/recsys-exports/open-jobs-export.service.ts's
 * stripHtml, but returns '' (never null) since callers here need a plain
 * string for RecPageState.jobDescription.
 */
export const stripHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  const text = html
    .replace(/<(style|script)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr|ul|ol|table)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&apos;/gi, "'")
    // decode &amp; last so double-encoded entities don't over-decode
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text;
};

/* ---- per-job-order saved search settings (kan-80) ------------------------ */

// Defaults the sanitizer falls back to per field. Kept next to DEFAULT_FILTERS
// so the two halves of "a search configuration" default consistently with
// context.tsx's INITIAL_STATE.
const DEFAULT_TOP_N = 20;
const DEFAULT_SEARCH_MODE: SearchMode = 'hybrid';
const SEARCH_MODES: SearchMode[] = ['boolean', 'semantic', 'hybrid'];
// Generous upper bound on stored must-have terms (the input caps entry at 16) —
// the row is user-controlled, so a hand-crafted payload can't flood the chips.
const MAX_SAVED_MUST_HAVE = 50;
const MAX_SAVED_SKILLS = 50;
// Per-string caps, same reasoning as the counts above: the row is shared, so a
// hand-crafted upsert must not be able to plant a multi-KB "skill" chip or
// geo label into every teammate's UI (and recsys request payload). React
// escaping already makes an oversized string harmless — this bounds it.
// Queries get more room: long boolean queries are legitimate.
const MAX_SAVED_TERM_CHARS = 500;
const MAX_SAVED_QUERY_CHARS = 2000;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const finiteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const cleanStringArray = (value: unknown, max: number): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim().slice(0, MAX_SAVED_TERM_CHARS))
    .filter((entry) => entry.length > 0)
    .slice(0, max);
};

/** Snaps a stored Top-N onto the select's option list (nearest, ties low). */
const snapTopN = (value: unknown): number => {
  const raw = finiteNumber(value);
  if (raw === null) return DEFAULT_TOP_N;
  return TOP_N_OPTIONS.reduce(
    (best, option) => (Math.abs(option - raw) < Math.abs(best - raw) ? option : best),
    TOP_N_OPTIONS[0]
  );
};

/**
 * Rebuilds a FilterState from stored JSON: every field is type-checked and
 * falls back to DEFAULT_FILTERS independently, unknown keys are dropped, and a
 * FilterState field added after the row was written simply keeps its default.
 * Geo is all-or-nothing (a lat without a lng is not a center), and radiusCustom
 * / geoCenterCustom carry through as stored so kan-78's "manual edit wins while
 * this job stays selected" semantics resume exactly where the saver left them.
 */
const sanitizeFilters = (raw: unknown): FilterState => {
  if (!isPlainObject(raw)) return { ...DEFAULT_FILTERS };

  const lat = finiteNumber(raw.lat);
  const lng = finiteNumber(raw.lng);
  const hasCoordinates = lat !== null && lng !== null;
  const minYears = finiteNumber(raw.minYears);
  const radiusMi = finiteNumber(raw.radiusMi);

  return {
    ...DEFAULT_FILTERS,
    minYears: minYears === null ? DEFAULT_FILTERS.minYears : Math.max(0, Math.round(minYears)),
    skills: cleanStringArray(raw.skills, MAX_SAVED_SKILLS),
    geoCenterLabel:
      typeof raw.geoCenterLabel === 'string'
        ? raw.geoCenterLabel.slice(0, MAX_SAVED_TERM_CHARS)
        : DEFAULT_FILTERS.geoCenterLabel,
    geoCenterCustom: raw.geoCenterCustom === true,
    lat: hasCoordinates ? lat : null,
    lng: hasCoordinates ? lng : null,
    radiusMi:
      hasCoordinates && radiusMi !== null
        ? Math.min(Math.max(Math.round(radiusMi), MIN_RADIUS_MI), MAX_RADIUS_MI)
        : MAX_RADIUS_MI,
    radiusCustom: raw.radiusCustom === true,
  };
};

/**
 * The page's complete search configuration, as persisted per job order on every
 * executed search (kan-80). Pure — the caller owns the network write.
 */
export const buildSearchSettingsPayload = (
  state: SavedSearchSettingsState
): SavedSearchSettingsV1 => ({
  schemaVersion: SEARCH_SETTINGS_SCHEMA_VERSION,
  mode: state.mode,
  topN: state.topN,
  mustHave: [...state.mustHave],
  filters: { ...state.filters },
  search: { mode: state.search.mode, query: state.search.query },
});

/**
 * Tolerant reader for a stored settings row: unknown fields are dropped and
 * missing/wrong-typed ones fall back to this page's defaults, so rows written
 * by an older (or newer) version of the page still load. Never throws. Returns
 * null when the payload is not a JSON object at all (array/scalar/null) — the
 * caller then keeps the job-order geo prefill as its starting state.
 */
export const sanitizeSavedSearchSettings = (raw: unknown): SavedSearchSettingsV1 | null => {
  if (!isPlainObject(raw)) return null;

  const search = isPlainObject(raw.search) ? raw.search : {};
  const searchMode = SEARCH_MODES.includes(search.mode as SearchMode)
    ? (search.mode as SearchMode)
    : DEFAULT_SEARCH_MODE;

  return {
    schemaVersion: SEARCH_SETTINGS_SCHEMA_VERSION,
    mode: raw.mode === 'search' ? 'search' : 'batch',
    topN: snapTopN(raw.topN),
    mustHave: cleanStringArray(raw.mustHave, MAX_SAVED_MUST_HAVE),
    filters: sanitizeFilters(raw.filters),
    search: {
      mode: searchMode,
      query:
        typeof search.query === 'string' ? search.query.slice(0, MAX_SAVED_QUERY_CHARS) : '',
    },
  };
};

// ---------------------------------------------------------------------------
// kan-119 follow-up: nightly maintenance window + search-backend error copy.
// The recsys corpus is published and re-indexed once a night (kan-76,
// infra/recsys/env.prod.sh "NIGHTLY WINDOW"): spool drain 04:00 UTC,
// reconcile 04:10, compaction 04:45, index build 05:00, serving bounce 07:00.
// Between the first publish and the bounce a freshly started recsys-api
// instance cannot boot (no prebuilt index for the moved manifest), so searches
// can fail or return nothing. The window is FIXED UTC on purpose (the crons
// are Etc/UTC), so it shifts an hour against Pacific time twice a year.
// ---------------------------------------------------------------------------

/** Start/end of the nightly window, UTC. End includes the ~10 min revision roll after the 07:00 bounce. */
export const NIGHTLY_MAINTENANCE_UTC = { startHour: 4, startMinute: 0, endHour: 7, endMinute: 15 } as const;

export interface NightlyMaintenance {
  active: boolean;
  /** The window's start/end as absolute instants (today's or, before 04:00 UTC, today's upcoming one). */
  start: Date;
  end: Date;
}

/** Whether `now` falls inside the nightly maintenance window. Pure; `now` is injectable for tests. */
export const nightlyMaintenance = (now: Date = new Date()): NightlyMaintenance => {
  const { startHour, startMinute, endHour, endMinute } = NIGHTLY_MAINTENANCE_UTC;
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), startHour, startMinute));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), endHour, endMinute));
  return { active: now >= start && now < end, start, end };
};

/** "9:00 PM" style local-time label for a window edge. */
export const formatLocalTime = (d: Date): string =>
  d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/** What index.tsx records when a search call fails for a reason other than the guardrails. */
export interface SearchBackendError {
  /** HTTP status from recsys/the proxy, or null for a network-level failure. */
  status: number | null;
  /** True when the failure happened inside the nightly window — the copy then explains the window. */
  duringMaintenance: boolean;
}

/**
 * Classify a thrown search error into the page's error state. Returns null for
 * the errors the tabs already handle themselves (the 400 guardrails), so the
 * caller keeps its existing behaviour for those.
 */
export const classifySearchError = (err: unknown, now: Date = new Date()): SearchBackendError | null => {
  const status = err instanceof RecsysHttpError ? err.status : null;
  if (status === 400) return null;
  return { status, duringMaintenance: nightlyMaintenance(now).active };
};
